"use client";

import {
  BORDER,
  BORDER_HI,
  FONT_MONO,
  TEXT_DIM,
  TEXT_MUTE,
  VIOLET,
} from "./palette";
import type { TempoLevel } from "./tempo-drill-helpers";

type Props = {
  levels: readonly TempoLevel[];
  levelIdx: number;
  running: boolean;
  onReset: () => void;
  onTogglePlay: () => void;
  onSelectLevel: (idx: number) => void;
};

export function ChordPracticeTempoControls({
  levels,
  levelIdx,
  running,
  onReset,
  onTogglePlay,
  onSelectLevel,
}: Props) {
  return (
    <div
      className="relative z-10 flex shrink-0 items-center justify-between"
      style={{
        padding: "12px 24px",
        borderTop: `1px solid ${BORDER}`,
        background: "rgba(10, 10, 14, 0.6)",
        gap: 16,
      }}
    >
      <button
        type="button"
        onClick={onReset}
        style={{
          padding: "7px 13px",
          borderRadius: 8,
          background: "transparent",
          border: `1px solid ${BORDER_HI}`,
          color: TEXT_DIM,
          fontSize: 11,
          cursor: "pointer",
          fontFamily: "inherit",
          flexShrink: 0,
        }}
      >
        ↺ Réinitialiser
      </button>

      <div className="flex items-center" style={{ gap: 6 }}>
        {levels.map((lvl, i) => (
          <LevelPill
            key={i}
            level={lvl}
            isActive={i === levelIdx}
            isPassed={i < levelIdx}
            onClick={() => onSelectLevel(i)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={onTogglePlay}
        style={{
          background: running ? "#1c1c23" : VIOLET,
          border: `1px solid ${running ? BORDER_HI : VIOLET}`,
          color: "#fff",
          fontSize: 11,
          padding: "7px 18px",
          borderRadius: 8,
          cursor: "pointer",
          fontWeight: 600,
          letterSpacing: "0.05em",
          fontFamily: "inherit",
          flexShrink: 0,
        }}
      >
        {running ? "❚❚ Pause" : "▶ Démarrer"}
      </button>
    </div>
  );
}

type LevelPillProps = {
  level: TempoLevel;
  isActive: boolean;
  isPassed: boolean;
  onClick: () => void;
};

function LevelPill({ level, isActive, isPassed, onClick }: LevelPillProps) {
  let bg: string = "transparent";
  let borderColor: string = BORDER;
  let bpmColor: string = TEXT_MUTE;
  let tierColor: string = TEXT_MUTE;

  if (isActive) {
    bg = `${level.color}1a`;
    borderColor = level.color;
    bpmColor = level.color;
    tierColor = level.color;
  } else if (isPassed) {
    borderColor = "rgba(255,255,255,0.10)";
    bpmColor = TEXT_DIM;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 6,
        padding: "5px 10px",
        borderRadius: 999,
        background: bg,
        border: `1px solid ${borderColor}`,
        cursor: "pointer",
        fontFamily: FONT_MONO,
        boxShadow: isActive ? `0 0 10px ${level.color}30` : "none",
        transition:
          "background-color 160ms ease, border-color 160ms ease, color 160ms ease",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 600, color: bpmColor }}>
        {level.bpm}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 600,
          color: tierColor,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
        }}
      >
        {level.tier}
      </span>
    </button>
  );
}
